import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Mail, MapPin } from 'lucide-react';
import { SITE_GUTTER } from '../styles/layout';

const GOLD = '#C9A84C';
const LINK_GRAY = '#F5F0E8';
const TEXT_LIGHT = '#F5F0E8';
const GOLD_BORDER = 'rgba(201, 168, 76, 0.3)';

const FooterLink = ({ to, children }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <Link
      to={to}
      style={{
        color: hovered ? GOLD : LINK_GRAY,
        textDecoration: 'none',
        transition: 'color 0.2s ease',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {children}
    </Link>
  );
};

const ContactRow = ({ href, icon: Icon, children }) => {
  const [hovered, setHovered] = useState(false);
  const content = (
    <>
      <Icon size={16} style={{ color: GOLD, flexShrink: 0 }} />
      <span style={{ color: hovered ? GOLD : TEXT_LIGHT, transition: 'color 0.2s ease' }}>
        {children}
      </span>
    </>
  );

  const rowStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.625rem',
    marginBottom: '0.75rem',
    fontSize: '0.9375rem',
    lineHeight: 1.4,
  };

  if (href) {
    return (
      <a
        href={href}
        style={{ ...rowStyle, textDecoration: 'none' }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {content}
      </a>
    );
  }

  return <div style={rowStyle}>{content}</div>;
};

const Footer = () => {
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  if (location.pathname.startsWith('/admin')) {
    return null;
  }

  const sectionHeading = {
    fontSize: '1.125rem',
    fontWeight: '600',
    marginBottom: '1rem',
    color: GOLD,
    marginTop: 0,
  };

  return (
    <footer
      className="dd-footer"
      style={{
        backgroundColor: '#000000',
        color: '#F5F0E8',
        padding: isMobile ? '2.5rem 1rem 2rem' : `clamp(2.5rem, 6vw, 3.5rem) ${SITE_GUTTER}`,
        borderTop: `1px solid ${GOLD_BORDER}`,
      }}
    >
      <div
        style={{
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile
              ? '1fr'
              : 'minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1.25fr)',
            gap: isMobile ? '2rem' : 'clamp(2rem, 5vw, 4rem)',
            alignItems: 'start',
            width: '100%',
          }}
        >
          {/* Brand */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
              <img
                src="/logo.webp"
                alt="Digital Divot"
                style={{
                  width: '2rem',
                  height: '2rem',
                  marginRight: '0.5rem',
                  objectFit: 'contain',
                  border: 'none',
                  outline: 'none',
                }}
              />
              <span
                style={{
                  fontSize: '1.25rem',
                  fontWeight: '700',
                  color: GOLD,
                  letterSpacing: '0.02em',
                }}
              >
                Digital Divot
              </span>
            </div>
            <p
              style={{
                color: TEXT_LIGHT,
                margin: '0 0 1.25rem',
                maxWidth: isMobile ? '22rem' : '28rem',
                lineHeight: 1.65,
                fontSize: '0.9375rem',
              }}
            >
              Bringing the golf course to your doorstep with our premium mobile golf simulators.
              Perfect for events, parties, and corporate functions.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 style={sectionHeading}>Quick Links</h3>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {[
                { to: '/', label: 'Home' },
                { to: '/about', label: 'About Us' },
                { to: '/Catalog', label: 'Catalog' },
                { to: '/packages', label: 'The Experience' },
                { to: '/guides', label: 'Guides' },
                { to: '/find-booking', label: 'Find Booking' },
              ].map(({ to, label }) => (
                <li key={to} style={{ marginBottom: '0.5rem' }}>
                  <FooterLink to={to}>{label}</FooterLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 style={sectionHeading}>Contact</h3>
            <ContactRow href="tel:+14809233349" icon={Phone}>
              (480) 923-3349
            </ContactRow>
            <ContactRow href="mailto:admin@digitaldivotgolf.com" icon={Mail}>
              admin@digitaldivotgolf.com
            </ContactRow>
            <ContactRow icon={MapPin}>Phoenix &amp; Scottsdale Metro, Arizona</ContactRow>

            <Link
              to="/booking"
              className="dd-footer-book-btn"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 'auto',
                flex: '0 0 auto',
                marginTop: '0.5rem',
                backgroundColor: GOLD,
                color: '#000000',
                padding: '0.625rem 1.5rem',
                borderRadius: '9999px',
                textDecoration: 'none',
                fontWeight: '700',
                fontSize: '0.9375rem',
              }}
            >
              Book Now
            </Link>
          </div>
        </div>

        <div
          style={{
            borderTop: '1px solid rgba(201, 168, 76, 0.15)',
            marginTop: isMobile ? '2rem' : '2.5rem',
            paddingTop: '1.5rem',
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '0.75rem 1.5rem',
            textAlign: 'center',
          }}
        >
          <p style={{ color: '#9CA3AF', margin: 0, fontSize: '0.875rem' }}>
            © {new Date().getFullYear()} Digital Divot. All rights reserved.
          </p>
          <FooterLink to="/privacy">Privacy Policy</FooterLink>
          <FooterLink to="/terms">Terms of Service</FooterLink>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
